/**
 * "What's new" — a core `panelPanes` entry (P1): the assistant's own
 * release notes, newest first, each headed by the assistant version it
 * belongs to and its date (the current version also shows in the header
 * button's title) (`agentChangelog` query; backend list in
 * `apps/backend/src/lib/agent/agentChangelog.ts`, the frozen legacy
 * releases merged with one file per newer release). Hidden entirely
 * (renders nothing) on a network error or a
 * `MissingCapabilities`/`MissingPermissions` refusal, same as
 * `useAgentAccess`/`AgentChatBox`'s other agent queries.
 *
 * Releases the user has not seen yet (remembered per browser, see
 * `agentChangelogSeen.ts`) carry a "New" chip; opening the pane marks every
 * shown release as seen, while the chips stay until the pane is opened again.
 *
 * Each item arrives as a stable id (e.g. `2026-09-assistant-1.chatPanel`)
 * that is a key of the Tolgee namespace `AssistantChangelog`; its value is
 * the end-user text (rewording = change the value, the key stays). The ids
 * are runtime strings, so they go through the dynamic-key cast
 * (`agentToolTitle.ts`/`PdsSyncReviewModal` precedent) with `orEmpty`: an
 * id Tolgee does not know yet yields "" and is hidden — never shown raw —
 * and a release left with no item is hidden too. There is no frontend
 * allow-list: the backend's `agentToolsBuild.spec.ts` checks every legacy
 * id exists in `AssistantChangelog/en.json`; a newer release's keys are
 * created on Tolgee from the English texts in its file and stay hidden
 * until they exist.
 */
import { NewReleases as NewReleasesIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { getDataOrNull } from "@msys/common";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { useEffect, useRef } from "react";
import { useAgentChangelogQuery } from "./AgentChangelog.generated.js";
import {
  agentChangelogQueryOptions,
  useVisibleChangelogReleases,
} from "./agentChangelogReleases.js";
import { useAgentChangelogUnseen } from "./agentChangelogSeen.js";

/**
 * The header button's tooltip/aria-label — registered as this pane's
 * `useTitle`. Carries the assistant's current version once the changelog is
 * loaded (the same query as the pane itself, so Apollo answers both from one
 * request); plain "What's new" while loading or when the query is refused.
 */
export function useAgentChangelogTitle(): string {
  const { t } = useTranslate(["Assistant"]);
  const { data } = useAgentChangelogQuery(agentChangelogQueryOptions());
  const currentVersion = data
    ? getDataOrNull(data.agentChangelog)?.currentVersion
    : undefined;
  return currentVersion
    ? t("What's new · v{version}", { ns: "Assistant", version: currentVersion })
    : t("What's new");
}

/** Drives the dot on the header button (this pane's `useBadge`): a release the user has not seen yet. */
export function useAgentChangelogUnseenBadge(): boolean {
  return useAgentChangelogUnseen().hasUnseen;
}

/** Whether the "What's new" pane is offered at all — always, once the panel itself is shown. */
export function useAgentChangelogVisible(): boolean {
  return true;
}

export const AgentChangelogIcon = NewReleasesIcon;

export function AgentChangelog() {
  const { t } = useTranslate(["Assistant", "AssistantChangelog"]);
  const { getFormattedDate } = useFormatting();
  const releases = useVisibleChangelogReleases();
  const { unseenIds, markSeen } = useAgentChangelogUnseen();

  // The markers are fixed to what was unseen when the list first showed, so
  // they stay while this view is open even though marking it seen (below)
  // clears the badge and the launcher dot at once. The visible release ids are
  // captured with them: a release that arrives while the pane is open (a
  // refetch) is not marked seen, it is marked the next time the pane opens.
  const markedIds = useRef<readonly string[] | null>(null);
  const visibleIdsOnMount = useRef<readonly string[]>([]);
  if (releases && markedIds.current === null) {
    markedIds.current = unseenIds;
    visibleIdsOnMount.current = releases.map(release => release.id);
  }

  const hasReleases = releases !== null;
  useEffect(() => {
    if (visibleIdsOnMount.current.length > 0)
      markSeen(visibleIdsOnMount.current);
  }, [hasReleases, markSeen]);

  if (!releases) return null; // loading, a query error, or a MissingCapabilities/MissingPermissions refusal

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">{t("What's new")}</Typography>
      {releases.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("No news yet")}
        </Typography>
      )}
      {releases.map(release => (
        <Box key={release.id}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 0.5, alignItems: "center" }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t("v{version} · {date}", {
                ns: "Assistant",
                version: release.version,
                date: getFormattedDate(release.releasedOn),
              })}
            </Typography>
            {markedIds.current?.includes(release.id) && (
              <Chip
                size="small"
                color="primary"
                label={t("New", { ns: "Assistant" })}
              />
            )}
          </Stack>
          {/* The bullet indent is the list's padding: a margin on the
              full-width items pushed them past the pane's right edge. The
              text may shrink and breaks long (German) words, so the pane
              never gets wider than the panel. */}
          <List dense disablePadding sx={{ pl: 2.5 }}>
            {release.items.map(item => (
              <ListItem
                key={item.id}
                disableGutters
                sx={{
                  display: "list-item",
                  listStyleType: "disc",
                  py: 0.25,
                }}
              >
                <ListItemText
                  primary={item.text}
                  sx={{ minWidth: 0 }}
                  slotProps={{
                    primary: { sx: { overflowWrap: "anywhere" } },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Stack>
  );
}
